import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { PatientCheckins } from "@/components/staff/PatientCheckins";
import { PreferenceSummary, MealSwapPicker } from "@/components/staff/MealPlanTools";
import { PatientSwapRequests, AvoidedMeals } from "@/components/staff/SwapRequests";
import { SignaturePicker } from "@/components/staff/SignaturePicker";
import { RemovePatientButton } from "@/components/staff/RemovePatientDialog";
import { SendIntakeForm } from "@/components/staff/SendIntakeForm";
import { RequestReviewButton } from "@/components/staff/RequestReviewButton";
import { shareLink } from "@/lib/pageMeta";
import { canDeleteReport, deleteReportWithUndo } from "@/components/staff/reportActions";
import {
  ArrowLeft, User, Heart, Activity, ClipboardList, Utensils,
  FileText, MessageSquare, Calendar, Plus, Save, Loader2,
  Droplet, Weight, Ruler, Stethoscope, Clock, ChevronRight,
  AlertCircle, CheckCircle2, Upload, Download, Trash2, CalendarClock,
  ChevronDown, ChevronUp, Sparkles, Apple, Dumbbell, Pill, Phone,
} from "lucide-react";

// ===== WHATSAPP HELPER =====
const openWhatsApp = (phone, message) => {
  const cleaned = (phone || "").replace(/[^0-9]/g, "");
  if (!cleaned) { toast.error("No phone number for this patient."); return; }
  const url = `https://wa.me/${cleaned}?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank");
};

// ===== COLLAPSIBLE SECTION =====
const Section = ({ title, subtitle, icon: Icon, children, defaultOpen = false, badge, id }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="rounded-xl border-stone-200/60 overflow-hidden" id={id} data-testid={`section-${id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-5 text-left hover:bg-stone-50/50 transition-colors"
        data-testid={`toggle-${id}`}
      >
        {Icon && (
          <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
            <Icon className="w-[18px] h-[18px] text-stone-600" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-stone-900">{title}</h2>
            {badge}
          </div>
          {subtitle && <p className="text-xs text-stone-500 mt-0.5">{subtitle}</p>}
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-stone-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-stone-400 shrink-0" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-stone-100 pt-4">{children}</div>}
    </Card>
  );
};

// ===== FIELD HELPERS =====
const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-stone-700 mb-1.5">{label}</label>
    {children}
  </div>
);

const ReadField = ({ label, value }) => (
  <div>
    <p className="text-xs text-stone-400">{label}</p>
    <p className="text-sm text-stone-700 mt-0.5">{value || "Not provided"}</p>
  </div>
);

// ===== MAIN COMPONENT =====
const PatientProfile = () => {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const openReportId = searchParams.get("report");
  const { token } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consultRecord, setConsultRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { ...headers, "Content-Type": "application/json" };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API}/staff/patient/${patientId}`, { headers });
      if (res.ok) setProfile(await res.json());
      else toast.error("Could not load patient profile.");
    } catch (err) { console.error(err); toast.error("Failed to load profile."); }
    finally { setLoading(false); }
  }, [patientId, token]);

  const fetchConsultRecord = useCallback(async () => {
    try {
      const res = await fetch(`${API}/staff/consultation-records/${patientId}/active`, { headers });
      if (res.ok) setConsultRecord(await res.json());
    } catch (e) { console.error(e); }
  }, [patientId, token]);

  useEffect(() => { fetchProfile(); fetchConsultRecord(); }, [fetchProfile, fetchConsultRecord]);

  // Auto-save consultation section
  const saveSection = async (sectionKey, data) => {
    if (!consultRecord?.record_id) return;
    setSaving(true);
    try {
      await fetch(`${API}/staff/consultation-records/${consultRecord.record_id}`, {
        method: "PUT", headers: jsonHeaders,
        body: JSON.stringify({ sections: { [sectionKey]: data } }),
      });
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const updateSection = (sectionKey, field, value) => {
    setConsultRecord(prev => {
      const sections = { ...prev.sections, [sectionKey]: { ...prev.sections?.[sectionKey], [field]: value } };
      return { ...prev, sections };
    });
  };

  const generateAISummary = async (summaryType = "clinical") => {
    if (!consultRecord?.record_id) return;
    toast.info("Generating AI summary...");
    try {
      const res = await fetch(`${API}/staff/consultation-records/${consultRecord.record_id}/ai-summary?summary_type=${summaryType}`, {
        method: "POST", headers,
      });
      if (res.ok) {
        const data = await res.json();
        if (data.generated) {
          toast.success("Summary generated. You can edit it before saving.");
          fetchConsultRecord();
        } else {
          toast.warning(data.summary);
        }
      } else toast.error("AI generation failed.");
    } catch { toast.error("Connection error."); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;
  if (!profile) return (
    <div className="text-center py-20">
      <AlertCircle className="w-10 h-10 text-stone-300 mx-auto mb-3" />
      <p className="text-stone-500">Patient not found.</p>
      <Button variant="outline" className="mt-4" onClick={() => navigate("/admin/patients")}>Back to Patients</Button>
    </div>
  );

  const { patient, meals, glucose_logs, notes, consultation_notes, assessments, intake_forms, observations, meal_plans, messages, bookings, communications, documents, intake_schedules, enrolments } = profile;
  const s = { name: patient?.name, phone: patient?.phone, ...(consultRecord?.sections || {}) };

  // Latest observations helper
  const latestObs = (type) => observations?.find(o => o.observation_type === type);

  // Next appointment
  const now = new Date();
  const upcomingBooking = bookings?.filter(b => {
    if (b.status !== "confirmed") return false;
    try { return new Date(b.appointment_datetime) >= now; } catch { return false; }
  }).sort((a, b) => new Date(a.appointment_datetime) - new Date(b.appointment_datetime))[0];

  return (
    <div className="space-y-4" data-testid="patient-profile">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/patients")} className="mt-1 shrink-0 text-stone-500 hover:text-stone-900">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#FF7A00]/10 flex items-center justify-center shrink-0">
              <span className="text-lg font-bold text-[#FF7A00]">{(patient.name || "?")[0].toUpperCase()}</span>
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-stone-900 tracking-tight">{patient.name || "Unknown"}</h1>
              <p className="text-sm text-stone-500">{patient.email}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {saving && <Badge className="bg-amber-50 text-amber-700 text-xs"><Loader2 className="w-3 h-3 animate-spin mr-1" />Saving</Badge>}
          <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => setShowTimeline(!showTimeline)}>
            <Calendar className="w-3.5 h-3.5 mr-1" /> Timeline
          </Button>
          <RequestReviewButton patientId={patientId} token={token} />
          <RemovePatientButton patient={{ ...patient, user_id: patientId }} token={token} />
        </div>
      </div>

      {/* Upcoming appointment banner */}
      {upcomingBooking && (
        <Card className="rounded-xl border-[#FF7A00]/30 bg-[#FF7A00]/5 p-4" data-testid="upcoming-appointment">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CalendarClock className="w-5 h-5 text-[#FF7A00]" />
              <div>
                <h3 className="text-sm font-bold text-stone-900 flex items-center gap-2">Next appointment
                  {upcomingBooking.attendance_confirmed_at && <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px]" data-testid="attendance-confirmed-badge"><CheckCircle2 className="w-3 h-3 mr-1" />Confirmed by patient</Badge>}
                </h3>
                <p className="text-xs text-stone-500">
                  {new Date(upcomingBooking.appointment_datetime).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                  {" at "}
                  {new Date(upcomingBooking.appointment_datetime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
            {upcomingBooking.meeting_url ? (
              <a href={upcomingBooking.meeting_url} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg text-sm" data-testid="join-meeting-btn">
                  <Activity className="w-4 h-4 mr-1.5" /> Join Meeting
                </Button>
              </a>
            ) : (
              <Badge variant="outline" className="text-xs text-stone-500">Meeting link pending</Badge>
            )}
          </div>
        </Card>
      )}

      {/* Programme Timeline (always visible) */}
      <ProgrammeTimeline patient={patient} consultation_notes={consultation_notes} observations={observations} bookings={bookings} assessments={assessments} intake_schedules={intake_schedules} enrolments={enrolments} />

      {/* ===== SECTION 1: PATIENT DETAILS ===== */}
      <Section title="Patient details" subtitle="Contact information, location, communication preferences." icon={User} id="details" defaultOpen>
        <div className="grid sm:grid-cols-3 gap-4">
          <ReadField label="Name" value={patient.name} />
          <ReadField label="Email" value={patient.email} />
          <ReadField label="Phone" value={patient.phone} />
          <ReadField label="Location" value={patient.location} />
          <ReadField label="Preferred channel" value={patient.preferred_channel} />
          <ReadField label="Programme" value={patient.program_type || "General"} />
          <ReadField label="Joined" value={patient.created_at?.split("T")[0]} />
        </div>
      </Section>

      {/* ===== SECTION 2: PRE-CONSULTATION DATA ===== */}
      <Section title="Pre-consultation data" subtitle="Intake form responses. Editable by the dietitian." icon={ClipboardList} id="preconsult">
        <PreConsultSection intakeForms={intake_forms} patientId={patientId} token={token} onRefresh={fetchProfile} s={s} updateSection={updateSection} saveSection={saveSection} />
      </Section>

      {/* ===== SECTION 3: MEDICAL HISTORY ===== */}
      <Section title="Medical history" subtitle="Diagnosed conditions, medications, allergies, family history." icon={Heart} id="medical">
        <div className="space-y-4">
          <Field label="Diagnosed conditions" className="w-full">
            <div className="flex flex-wrap gap-2 mb-2">
              {["Hypertension", "Type 2 diabetes", "Dyslipidaemia", "Obesity", "Pre-diabetes", "Cardiovascular disease", "Kidney disease"].map(c => {
                const selected = (s.medical_history?.conditions || []).includes(c);
                return (
                  <button key={c} onClick={() => {
                    const cur = s.medical_history?.conditions || [];
                    const next = selected ? cur.filter(x => x !== c) : [...cur, c];
                    updateSection("medical_history", "conditions", next);
                  }} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selected ? "bg-[#FF7A00]/10 border-[#FF7A00]/30 text-[#FF7A00]" : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"}`}>
                    {c}
                  </button>
                );
              })}
            </div>
            <Input placeholder="Other conditions..." value={s.medical_history?.other_conditions || ""} onChange={e => updateSection("medical_history", "other_conditions", e.target.value)} onBlur={() => saveSection("medical_history", s.medical_history)} className="rounded-lg" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Current medications">
              <Textarea placeholder="List medications, dosages..." value={s.medical_history?.medications || ""} onChange={e => updateSection("medical_history", "medications", e.target.value)} onBlur={() => saveSection("medical_history", s.medical_history)} className="rounded-lg min-h-[80px]" />
            </Field>
            <Field label="Allergies and intolerances">
              <Textarea placeholder="Food allergies, drug allergies..." value={s.medical_history?.allergies || ""} onChange={e => updateSection("medical_history", "allergies", e.target.value)} onBlur={() => saveSection("medical_history", s.medical_history)} className="rounded-lg min-h-[80px]" />
            </Field>
          </div>
          <Field label="Family history">
            <Textarea placeholder="Relevant family medical history..." value={s.medical_history?.family_history || ""} onChange={e => updateSection("medical_history", "family_history", e.target.value)} onBlur={() => saveSection("medical_history", s.medical_history)} className="rounded-lg min-h-[60px]" />
          </Field>
        </div>
      </Section>

      {/* ===== SECTION 4: MEASUREMENTS ===== */}
      <Section title="Anthropometric measurements" subtitle="Weight, height, BMI, blood pressure, glucose, lipid panel." icon={Activity} id="measurements">
        <MeasurementsSection s={s} updateSection={updateSection} saveSection={saveSection} observations={observations} patientId={patientId} token={token} onRefresh={fetchProfile} />
      </Section>

      {/* ===== SECTION 5: LIFESTYLE FACTORS (moved before dietary) ===== */}
      <Section title="Lifestyle factors" subtitle="Physical activity, sleep, stress, alcohol, smoking." icon={Dumbbell} id="lifestyle">
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Physical activity level">
              <Select value={s.lifestyle?.physical_activity || ""} onValueChange={v => { updateSection("lifestyle", "physical_activity", v); setTimeout(() => saveSection("lifestyle", { ...s.lifestyle, physical_activity: v }), 100); }}>
                <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sedentary">Sedentary</SelectItem>
                  <SelectItem value="light">Light (1-2 days/week)</SelectItem>
                  <SelectItem value="moderate">Moderate (3-4 days/week)</SelectItem>
                  <SelectItem value="active">Active (5+ days/week)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sleep quality">
              <Select value={s.lifestyle?.sleep_quality || ""} onValueChange={v => { updateSection("lifestyle", "sleep_quality", v); setTimeout(() => saveSection("lifestyle", { ...s.lifestyle, sleep_quality: v }), 100); }}>
                <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good (7-9 hours, restful)</SelectItem>
                  <SelectItem value="fair">Fair (5-7 hours, some disruption)</SelectItem>
                  <SelectItem value="poor">Poor (under 5 hours or very disrupted)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Stress level">
              <Select value={s.lifestyle?.stress_level || ""} onValueChange={v => { updateSection("lifestyle", "stress_level", v); setTimeout(() => saveSection("lifestyle", { ...s.lifestyle, stress_level: v }), 100); }}>
                <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Alcohol use">
              <Input placeholder="e.g. Social, none, daily..." value={s.lifestyle?.alcohol || ""} onChange={e => updateSection("lifestyle", "alcohol", e.target.value)} onBlur={() => saveSection("lifestyle", s.lifestyle)} className="rounded-lg" />
            </Field>
            <Field label="Smoking status">
              <Select value={s.lifestyle?.smoking || ""} onValueChange={v => { updateSection("lifestyle", "smoking", v); setTimeout(() => saveSection("lifestyle", { ...s.lifestyle, smoking: v }), 100); }}>
                <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never smoked</SelectItem>
                  <SelectItem value="former">Former smoker</SelectItem>
                  <SelectItem value="current">Current smoker</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Daily water intake">
            <Input placeholder="e.g. 1.5 litres" value={s.lifestyle?.water_intake || ""} onChange={e => updateSection("lifestyle", "water_intake", e.target.value)} onBlur={() => saveSection("lifestyle", s.lifestyle)} className="rounded-lg" />
          </Field>
        </div>
      </Section>

      {/* ===== SECTION 6: DIETARY ASSESSMENT ===== */}
      <Section title="Dietary assessment" subtitle="Current eating patterns, typical meals, cooking methods." icon={Utensils} id="dietary">
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Usual meal pattern (meals per day)">
              <Input placeholder="e.g. 3 meals, 1 snack" value={s.dietary_assessment?.meal_pattern || ""} onChange={e => updateSection("dietary_assessment", "meal_pattern", e.target.value)} onBlur={() => saveSection("dietary_assessment", s.dietary_assessment)} className="rounded-lg" />
            </Field>
            <Field label="Who prepares food?">
              <Input placeholder="Self, spouse, helper..." value={s.dietary_assessment?.food_preparer || ""} onChange={e => updateSection("dietary_assessment", "food_preparer", e.target.value)} onBlur={() => saveSection("dietary_assessment", s.dietary_assessment)} className="rounded-lg" />
            </Field>
          </div>
          <Field label="Typical meals (describe a usual day)">
            <Textarea placeholder="Breakfast: ..., Lunch: ..., Dinner: ..." value={s.dietary_assessment?.typical_meals || ""} onChange={e => updateSection("dietary_assessment", "typical_meals", e.target.value)} onBlur={() => saveSection("dietary_assessment", s.dietary_assessment)} className="rounded-lg min-h-[100px]" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Cooking methods">
              <Input placeholder="Frying, boiling, grilling..." value={s.dietary_assessment?.cooking_methods || ""} onChange={e => updateSection("dietary_assessment", "cooking_methods", e.target.value)} onBlur={() => saveSection("dietary_assessment", s.dietary_assessment)} className="rounded-lg" />
            </Field>
            <Field label="Eating out frequency">
              <Input placeholder="e.g. 2-3 times a week" value={s.dietary_assessment?.eating_out || ""} onChange={e => updateSection("dietary_assessment", "eating_out", e.target.value)} onBlur={() => saveSection("dietary_assessment", s.dietary_assessment)} className="rounded-lg" />
            </Field>
          </div>
          <Field label="Food budget and access considerations">
            <Textarea placeholder="Budget constraints, food availability..." value={s.dietary_assessment?.food_budget || ""} onChange={e => updateSection("dietary_assessment", "food_budget", e.target.value)} onBlur={() => saveSection("dietary_assessment", s.dietary_assessment)} className="rounded-lg min-h-[60px]" />
          </Field>
        </div>
      </Section>

      {/* ===== SECTION 7: MEAL PREFERENCES (OncoNourish-style food selector) ===== */}
      <Section title="Meal preferences" subtitle="Tick the foods and drinks the patient enjoys or would like in their meal plan." icon={Apple} id="preferences">
        <FoodPreferencesSection s={s} updateSection={updateSection} saveSection={saveSection} patientId={patientId} token={token} />
      </Section>

      {/* ===== SECTION 8: CLINICAL NOTES ===== */}
      <Section title="Clinical notes" subtitle="Admission, progress, and discharge notes." icon={Stethoscope} id="clinical-notes" badge={consultation_notes?.length > 0 && <Badge variant="outline" className="text-xs">{consultation_notes.length} on file</Badge>}>
        <ClinicalNotesSection consultation_notes={consultation_notes} notes={notes} patientId={patientId} token={token} onRefresh={fetchProfile} />
      </Section>

      {/* ===== SECTION 9: DIETITIAN SUMMARY ===== */}
      <Section title="Dietitian summary" subtitle="Your clinical summary. Can be generated by AI and edited." icon={FileText} id="dietitian-summary">
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => generateAISummary("clinical")} data-testid="generate-clinical-summary">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Generate AI summary
            </Button>
          </div>
          <Textarea
            placeholder="Your clinical summary in your own voice..."
            value={s.dietitian_summary || ""}
            onChange={e => setConsultRecord(prev => ({ ...prev, sections: { ...prev.sections, dietitian_summary: e.target.value } }))}
            onBlur={() => saveSection("dietitian_summary", s.dietitian_summary)}
            className="rounded-lg min-h-[150px]"
            data-testid="dietitian-summary-field"
          />
        </div>
      </Section>

      {/* ===== SECTION 10: TREATMENT PLAN ===== */}
      <Section title="Treatment plan" subtitle="Clinical goals, dietary recommendations, agreed changes." icon={ClipboardList} id="treatment-plan">
        <div className="space-y-4">
          <Field label="Clinical goals">
            <Textarea placeholder="e.g. Reduce HbA1c to below 7%, manage blood pressure..." value={s.treatment_plan?.goals || ""} onChange={e => updateSection("treatment_plan", "goals", e.target.value)} onBlur={() => saveSection("treatment_plan", s.treatment_plan)} className="rounded-lg min-h-[80px]" />
          </Field>
          <Field label="Dietary recommendations">
            <Textarea placeholder="Specific dietary changes, portion guidance, food swaps..." value={s.treatment_plan?.dietary_recommendations || ""} onChange={e => updateSection("treatment_plan", "dietary_recommendations", e.target.value)} onBlur={() => saveSection("treatment_plan", s.treatment_plan)} className="rounded-lg min-h-[80px]" />
          </Field>
          <Field label="Specific changes agreed with patient">
            <Textarea placeholder="Concrete next steps agreed during the consultation..." value={s.treatment_plan?.specific_changes || ""} onChange={e => updateSection("treatment_plan", "specific_changes", e.target.value)} onBlur={() => saveSection("treatment_plan", s.treatment_plan)} className="rounded-lg min-h-[80px]" />
          </Field>
        </div>
      </Section>

      {/* ===== SECTION 11: MEAL PLANS ===== */}
      <Section title="Meal plans" subtitle="AI-generated meal plans, review and approval." icon={Utensils} id="meal-plans" defaultOpen={window.location.hash === "#meal-plans"} badge={meal_plans?.length > 0 && <Badge variant="outline" className="text-xs">{meal_plans.length} plans</Badge>}>
        <MealPlansSection meal_plans={meal_plans} patientId={patientId} token={token} onRefresh={fetchProfile} s={s} />
      </Section>

      {/* ===== SECTION 13: REPORTS ===== */}
      <Section title="Reports" subtitle="Clinical reports, progress summaries, discharge documents." icon={FileText} id="reports" defaultOpen={!!openReportId}>
        <ReportsSection patientId={patientId} token={token} onRefresh={fetchProfile} patient={patient} s={s} />
      </Section>

      {/* ===== SECTION 14: COMMUNICATIONS ===== */}
      <Section title="Weekly check-ins" subtitle="Preview each check-in form or send it to the patient now." icon={ClipboardList} id="checkins">
        <PatientCheckins patientId={patientId} token={token} />
      </Section>

      <Section title="Communications" subtitle="Emails, WhatsApp messages, and delivery status." icon={MessageSquare} id="communications" badge={communications?.length > 0 && <Badge variant="outline" className="text-xs">{communications.length} events</Badge>}>
        <CommunicationsSection communications={communications} intake_schedules={intake_schedules} />
      </Section>
    </div>
  );
};

// ===== SUB-SECTIONS (extracted for readability) =====

const PreConsultSection = ({ intakeForms, patientId, token, onRefresh, s, updateSection, saveSection }) => {
  const latestAssessment = intakeForms?.[0];
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const res = await fetch(`${API}/staff/patient/${patientId}/documents`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) { const d = await res.json(); setDocuments(d.documents || []); }
      } catch (e) { console.error(e); }
    };
    fetchDocs();
  }, [patientId, token]);

  const getValue = (key) => s?.preconsult?.[key] || latestAssessment?.[key] || "";

  const sv = () => saveSection("preconsult", s?.preconsult);
  const up = (key, val) => updateSection("preconsult", key, val);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {latestAssessment ? (
          <Badge variant="outline" className="text-xs bg-green-50 text-green-700" data-testid="intake-submitted-badge">Patient submitted {new Date(latestAssessment.submitted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</Badge>
        ) : (
          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700" data-testid="intake-not-submitted-badge">Not yet submitted by patient</Badge>
        )}
        <GenerateIntakeLink patientId={patientId} token={token} s={s} />
      </div>
      <p className="text-xs text-stone-500">Pre-populated from the patient's intake form where available. Editable by the dietitian.</p>

      {/* Your details */}
      <div>
        <h3 className="text-sm font-bold text-stone-800 mb-3">Patient details</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Full name"><Input value={getValue("full_name")} onChange={e => up("full_name", e.target.value)} onBlur={sv} placeholder="Full name" className="rounded-lg" /></Field>
          <Field label="Date of birth"><Input type="date" value={getValue("date_of_birth")} onChange={e => up("date_of_birth", e.target.value)} onBlur={sv} className="rounded-lg" /></Field>
          <Field label="Phone"><Input value={getValue("phone")} onChange={e => up("phone", e.target.value)} onBlur={sv} placeholder="+234..." className="rounded-lg" /></Field>
          <Field label="Email"><Input value={getValue("email")} onChange={e => up("email", e.target.value)} onBlur={sv} placeholder="email@example.com" className="rounded-lg" /></Field>
          <Field label="Location"><Input value={getValue("location")} onChange={e => up("location", e.target.value)} onBlur={sv} placeholder="City, Country" className="rounded-lg" /></Field>
          <Field label="Preferred channel"><Input value={getValue("preferred_channel")} onChange={e => up("preferred_channel", e.target.value)} onBlur={sv} placeholder="WhatsApp / Email" className="rounded-lg" /></Field>
        </div>
      </div>

      {/* Health measurements */}
      <div>
        <h3 className="text-sm font-bold text-stone-800 mb-3">Health measurements</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Weight (kg)"><Input value={getValue("weight")} onChange={e => up("weight", e.target.value)} onBlur={sv} placeholder="e.g. 78" className="rounded-lg" /></Field>
          <Field label="Height (cm)"><Input value={getValue("height")} onChange={e => up("height", e.target.value)} onBlur={sv} placeholder="e.g. 170" className="rounded-lg" /></Field>
          <Field label="Blood pressure (mmHg)"><Input value={getValue("blood_pressure")} onChange={e => up("blood_pressure", e.target.value)} onBlur={sv} placeholder="e.g. 130/85" className="rounded-lg" /></Field>
          <Field label="Fasting blood glucose (mmol/L)"><Input value={getValue("fasting_glucose")} onChange={e => up("fasting_glucose", e.target.value)} onBlur={sv} placeholder="e.g. 5.6" className="rounded-lg" /></Field>
        </div>
        <div className="mt-3 space-y-3">
          <Field label="Lipid profile (if available)"><Input value={getValue("lipid_profile")} onChange={e => up("lipid_profile", e.target.value)} onBlur={sv} placeholder="Total cholesterol, LDL, HDL, triglycerides" className="rounded-lg" /></Field>
          <Field label="Other laboratory results"><Textarea value={getValue("other_lab_results")} onChange={e => up("other_lab_results", e.target.value)} onBlur={sv} placeholder="Any other recent test results" className="rounded-lg min-h-[60px]" /></Field>
        </div>
      </div>

      {/* Reason for consultation */}
      <div>
        <h3 className="text-sm font-bold text-stone-800 mb-3">Reason for consultation</h3>
        <div className="space-y-3">
          <Field label="Main health concerns"><Textarea value={getValue("main_health_concerns")} onChange={e => up("main_health_concerns", e.target.value)} onBlur={sv} placeholder="What health concerns would you like to address?" className="rounded-lg min-h-[60px]" /></Field>
          <Field label="Patient's goals"><Textarea value={getValue("personal_goals")} onChange={e => up("personal_goals", e.target.value)} onBlur={sv} placeholder="What the patient would like to achieve" className="rounded-lg min-h-[60px]" /></Field>
          <Field label="Reason for seeking nutrition support"><Textarea value={getValue("reason_for_support")} onChange={e => up("reason_for_support", e.target.value)} onBlur={sv} placeholder="What prompted them to seek support" className="rounded-lg min-h-[60px]" /></Field>
          <Field label="Previous nutrition interventions"><Textarea value={getValue("previous_nutrition_interventions")} onChange={e => up("previous_nutrition_interventions", e.target.value)} onBlur={sv} placeholder="Previous dietitian, specific diets tried" className="rounded-lg min-h-[60px]" /></Field>
        </div>
      </div>

      {/* Uploaded documents (lab results) */}
      <div>
        <h3 className="text-sm font-bold text-stone-800 mb-3">Uploaded documents and lab results</h3>
        {documents.length > 0 ? (
          <div className="space-y-2">
            {documents.map((d, i) => (
              <div key={i} className="flex items-center gap-3 bg-stone-50 rounded-lg px-3 py-2">
                <FileText className="w-4 h-4 text-[#FF7A00] shrink-0" />
                <span className="text-sm text-stone-700 flex-1 truncate">{d.original_filename || d.filename || "Document"}</span>
                <span className="text-xs text-stone-400">{d.created_at?.split("T")[0]}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-stone-400">No documents uploaded. The patient can upload lab results through the intake form.</p>
        )}
      </div>

      {/* Doctor communication */}
      <div>
        <h3 className="text-sm font-bold text-stone-800 mb-3">Doctor communication</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Doctor's name"><Input value={getValue("doctor_name")} onChange={e => up("doctor_name", e.target.value)} onBlur={sv} placeholder="Dr..." className="rounded-lg" /></Field>
          <Field label="Doctor's email"><Input value={getValue("doctor_email")} onChange={e => up("doctor_email", e.target.value)} onBlur={sv} placeholder="doctor@practice.com" className="rounded-lg" /></Field>
          <Field label="Practice or institution"><Input value={getValue("doctor_practice")} onChange={e => up("doctor_practice", e.target.value)} onBlur={sv} placeholder="Hospital / clinic name" className="rounded-lg" /></Field>
          <Field label="Doctor's phone"><Input value={getValue("doctor_phone")} onChange={e => up("doctor_phone", e.target.value)} onBlur={sv} className="rounded-lg" /></Field>
        </div>
      </div>

      {/* Consent status */}
      <div>
        <h3 className="text-sm font-bold text-stone-800 mb-3">Consent</h3>
        <div className="space-y-2">
          {[
            { key: "service_consent", label: "Service consent (required for programme)" },
            { key: "doctor_sharing_consent", label: "Consent to share reports with their doctor" },
            { key: "marketing_consent", label: "Marketing communications consent" },
          ].map(c => {
            const val = latestAssessment?.[c.key] || getValue(c.key);
            return (
              <div key={c.key} className="flex items-center gap-2">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${val ? "bg-green-100" : "bg-stone-100"}`}>
                  {val ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <AlertCircle className="w-3.5 h-3.5 text-stone-400" />}
                </div>
                <span className="text-sm text-stone-600">{c.label}</span>
                <Badge variant="outline" className={`text-[10px] ml-auto ${val ? "bg-green-50 text-green-700" : "bg-stone-50 text-stone-500"}`}>{val ? "Yes" : "Not given"}</Badge>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const GenerateIntakeLink = ({ patientId, token, s }) => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    fetch(`${API}/staff/intake/send-status/${patientId}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(setStatus).catch(() => setStatus({}));
  }, [patientId, token]);
  if (!status) return <Loader2 className="w-4 h-4 animate-spin text-stone-400" />;
  return <SendIntakeForm patientId={patientId} token={token} email={status.has_email ? "yes" : ""} phone={s?.phone || status.phone} lastSent={status.last_sent} showCopy />;
};

// ===== FOOD PREFERENCES (API-driven, editable food selector) =====
const FoodPreferencesSection = ({ s, updateSection, saveSection, patientId, token }) => {
  const selected = s.meal_preferences?.selected_foods || {};
  const [expandedCat, setExpandedCat] = useState(null);
  const [categories, setCategories] = useState([]);
  const [prefLink, setPrefLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [newFoodInput, setNewFoodInput] = useState({});
  const [newCatName, setNewCatName] = useState("");
  const [addingCat, setAddingCat] = useState(false);

  const staffHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchCategories = () => {
    fetch(`${API}/food-categories`).then(r => r.json()).then(d => setCategories(d.categories || [])).catch(() => {});
  };

  useEffect(() => { fetchCategories(); }, []);

  const toggleFood = (catName, food) => {
    const catFoods = selected[catName] || [];
    const next = catFoods.includes(food) ? catFoods.filter(f => f !== food) : [...catFoods, food];
    const newSelected = { ...selected, [catName]: next };
    updateSection("meal_preferences", "selected_foods", newSelected);
  };

  const handleSave = () => saveSection("meal_preferences", s.meal_preferences);

  const addFoodToCategory = async (categoryId, catName) => {
    const food = (newFoodInput[categoryId] || "").trim();
    if (!food) return;
    try {
      const res = await fetch(`${API}/staff/food-categories/${categoryId}/foods`, {
        method: "POST", headers: staffHeaders, body: JSON.stringify({ food }),
      });
      if (res.ok) {
        toast.success(`"${food}" added to ${catName}.`);
        setNewFoodInput(prev => ({ ...prev, [categoryId]: "" }));
        fetchCategories();
      }
    } catch { toast.error("Could not add food."); }
  };

  const addNewCategory = async () => {
    if (!newCatName.trim()) return;
    setAddingCat(true);
    try {
      const res = await fetch(`${API}/staff/food-categories`, {
        method: "POST", headers: staffHeaders, body: JSON.stringify({ name: newCatName.trim(), foods: [] }),
      });
      if (res.ok) { toast.success(`Category "${newCatName.trim()}" added.`); setNewCatName(""); fetchCategories(); }
    } catch { toast.error("Could not add category."); }
    finally { setAddingCat(false); }
  };

  const generatePrefLink = async () => {
    setGeneratingLink(true);
    try {
      const res = await fetch(`${API}/staff/food-preference-forms/generate-link?patient_id=${patientId}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const fullLink = shareLink(data.link);
        setPrefLink(fullLink);
        await navigator.clipboard.writeText(fullLink);
        toast.success("Food preference form link copied to clipboard.");
      }
    } catch { toast.error("Could not generate link."); }
    finally { setGeneratingLink(false); }
  };

  const totalSelected = Object.values(selected).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-stone-500">Tick the foods and drinks the patient enjoys or would like in their meal plan.</p>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="outline" className="text-xs">{totalSelected} selected</Badge>
          <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={generatePrefLink} disabled={generatingLink} data-testid="generate-food-pref-link">
            {generatingLink ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
            {prefLink ? "Copy link again" : "Generate link"}
          </Button>
          {prefLink && (
            <Button variant="outline" size="sm" className="text-xs rounded-lg text-green-700 border-green-200 hover:bg-green-50" onClick={() => {
              const name = s?.name || "there";
              openWhatsApp(s?.phone, `Hi ${name}, please fill out your food preferences using this link so we can create a personalised meal plan for you:\n\n${prefLink}\n\nPlateMind Nutrition`);
            }} data-testid="send-pref-whatsapp">
              <Phone className="w-3 h-3 mr-1" /> WhatsApp link
            </Button>
          )}
        </div>
      </div>
      {prefLink && <p className="text-xs text-green-600 bg-green-50 rounded-lg px-3 py-2 break-all">Link copied: {prefLink}</p>}

      <div className="space-y-2">
        {categories.map(cat => {
          const catSelected = selected[cat.name] || [];
          const isExpanded = expandedCat === cat.name;
          return (
            <div key={cat.category_id} className="border border-stone-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedCat(isExpanded ? null : cat.name)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-stone-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-stone-800">{cat.name}</span>
                  {catSelected.length > 0 && (
                    <Badge className="bg-[#FF7A00]/10 text-[#FF7A00] border-[#FF7A00]/20 text-[10px] px-1.5">{catSelected.length}</Badge>
                  )}
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-stone-100">
                  <div className="flex flex-wrap gap-1.5">
                    {(cat.foods || []).map(food => {
                      const isSelected = catSelected.includes(food);
                      return (
                        <button
                          key={food}
                          onClick={() => toggleFood(cat.name, food)}
                          onMouseUp={handleSave}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                            isSelected
                              ? "bg-[#FF7A00] border-[#FF7A00] text-white"
                              : "bg-white border-stone-200 text-stone-600 hover:border-stone-300 hover:bg-stone-50"
                          }`}
                        >
                          {food}
                        </button>
                      );
                    })}
                  </div>
                  {/* Inline add food */}
                  <div className="flex items-center gap-2 mt-3 pt-2 border-t border-stone-100">
                    <Input
                      placeholder={`Add food to ${cat.name}...`}
                      value={newFoodInput[cat.category_id] || ""}
                      onChange={e => setNewFoodInput(prev => ({ ...prev, [cat.category_id]: e.target.value }))}
                      onKeyDown={e => { if (e.key === "Enter") addFoodToCategory(cat.category_id, cat.name); }}
                      className="rounded-lg text-xs h-8 flex-1"
                    />
                    <Button size="sm" variant="outline" className="text-xs h-8 rounded-lg shrink-0" onClick={() => addFoodToCategory(cat.category_id, cat.name)}>
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add new category */}
      <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
        <Input
          placeholder="New category name..."
          value={newCatName}
          onChange={e => setNewCatName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") addNewCategory(); }}
          className="rounded-lg text-xs h-8 flex-1"
        />
        <Button size="sm" variant="outline" className="text-xs h-8 rounded-lg shrink-0" onClick={addNewCategory} disabled={addingCat} data-testid="add-food-category">
          {addingCat ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />} Add category
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-stone-100">
        <Field label="Dietary restrictions">
          <Input placeholder="Vegetarian, halal, lactose-free..." value={s.meal_preferences?.restrictions || ""} onChange={e => updateSection("meal_preferences", "restrictions", e.target.value)} onBlur={handleSave} className="rounded-lg" />
        </Field>
        <Field label="Religious or cultural considerations">
          <Input placeholder="Fasting periods, etc." value={s.meal_preferences?.religious || ""} onChange={e => updateSection("meal_preferences", "religious", e.target.value)} onBlur={handleSave} className="rounded-lg" />
        </Field>
      </div>
    </div>
  );
};


const MeasurementsSection = ({ s, updateSection, saveSection, observations, patientId, token, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [obsForm, setObsForm] = useState({ observation_type: "weight", value: "", unit: "kg" });
  const [saving, setSaving] = useState(false);
  const unitMap = { weight: "kg", height: "cm", blood_pressure: "mmHg", fasting_glucose: "mmol/L", lipid_profile: "mmol/L", bmi: "kg/m\u00B2" };
  const latestObs = (type) => observations?.find(o => o.observation_type === type);

  const handleAddObs = async () => {
    if (!obsForm.value) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/staff/clinical-observations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...obsForm, patient_id: patientId }),
      });
      if (res.ok) { toast.success("Recorded."); setShowAdd(false); setObsForm({ observation_type: "weight", value: "", unit: "kg" }); onRefresh(); }
    } catch { toast.error("Failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Quick measurements from consultation record */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {[
          { key: "weight", label: "Weight (kg)", placeholder: "e.g. 82" },
          { key: "height", label: "Height (cm)", placeholder: "e.g. 170" },
          { key: "bmi", label: "BMI", placeholder: "Auto or manual" },
          { key: "waist_circumference", label: "Waist (cm)", placeholder: "e.g. 95" },
          { key: "bp_systolic", label: "BP systolic", placeholder: "e.g. 140" },
          { key: "bp_diastolic", label: "BP diastolic", placeholder: "e.g. 90" },
          { key: "fasting_glucose", label: "Fasting glucose (mmol/L)", placeholder: "e.g. 7.2" },
          { key: "hba1c", label: "HbA1c (%)", placeholder: "e.g. 6.8" },
          { key: "total_cholesterol", label: "Total cholesterol", placeholder: "mmol/L" },
          { key: "ldl", label: "LDL", placeholder: "mmol/L" },
          { key: "hdl", label: "HDL", placeholder: "mmol/L" },
          { key: "triglycerides", label: "Triglycerides", placeholder: "mmol/L" },
        ].map(f => (
          <Field key={f.key} label={f.label}>
            <Input type="number" step="any" placeholder={f.placeholder} value={s.measurements?.[f.key] || ""} onChange={e => updateSection("measurements", f.key, e.target.value)} onBlur={() => saveSection("measurements", s.measurements)} className="rounded-lg" />
          </Field>
        ))}
      </div>

      {/* Historical observations */}
      {observations?.length > 0 && (
        <div className="pt-3 border-t border-stone-100">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Recent recorded values</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {["weight", "blood_pressure", "fasting_glucose", "bmi"].map(type => {
              const obs = latestObs(type);
              return obs ? (
                <div key={type} className="bg-stone-50 rounded-lg p-2">
                  <p className="text-xs text-stone-400 capitalize">{type.replace(/_/g, " ")}</p>
                  <p className="text-sm font-bold text-stone-800">{obs.value} {obs.unit}</p>
                  <p className="text-[10px] text-stone-400">{obs.date_recorded?.split("T")[0] || obs.created_at?.split("T")[0]}</p>
                </div>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const ClinicalNotesSection = ({ consultation_notes, notes, patientId, token, onRefresh }) => {
  const [showNew, setShowNew] = useState(false);
  const [noteType, setNoteType] = useState("initial");
  const [form, setForm] = useState({
    consultation_type: "initial", relevant_history: "", clinical_observations: "",
    nutrition_assessment: "", nutrition_problems: "", patient_goals: "",
    nutrition_intervention: "", monitoring_plan: "", follow_up_actions: "", free_text_notes: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (status = "draft") => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/staff/consultation-notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, patient_id: patientId, status }),
      });
      if (res.ok) { toast.success(`Note saved as ${status}.`); setShowNew(false); onRefresh(); }
      else toast.error("Failed to save.");
    } catch { toast.error("Connection error."); }
    finally { setSaving(false); }
  };

  const noteFields = [
    { key: "relevant_history", label: "Relevant history" },
    { key: "clinical_observations", label: "Clinical observations" },
    { key: "nutrition_assessment", label: "Nutrition assessment" },
    { key: "nutrition_problems", label: "Nutrition problems or diagnoses" },
    { key: "patient_goals", label: "Patient goals" },
    { key: "nutrition_intervention", label: "Nutrition intervention" },
    { key: "monitoring_plan", label: "Monitoring plan" },
    { key: "follow_up_actions", label: "Follow-up actions" },
    { key: "free_text_notes", label: "Additional notes" },
  ];

  const statusColor = { draft: "bg-yellow-50 text-yellow-700", reviewed: "bg-blue-50 text-blue-700", approved: "bg-green-50 text-green-700" };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {["initial", "progress", "discharge"].map(t => (
            <button key={t} onClick={() => { setNoteType(t); setForm(f => ({ ...f, consultation_type: t })); setShowNew(true); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${showNew && noteType === t ? "bg-[#FF7A00]/10 border-[#FF7A00]/30 text-[#FF7A00]" : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"}`}>
              {t === "initial" ? "Admission Note" : t === "progress" ? "Progress Note" : "Discharge Note"}
            </button>
          ))}
        </div>
      </div>

      {showNew && (
        <div className="space-y-3 bg-stone-50 rounded-lg p-4">
          {noteFields.map(f => (
            <Field key={f.key} label={f.label}>
              <Textarea placeholder={f.label + "..."} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} className="rounded-lg min-h-[60px] bg-white" />
            </Field>
          ))}
          <div className="flex gap-2 pt-2">
            <Button onClick={() => handleSave("draft")} disabled={saving} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg text-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save as draft
            </Button>
            <Button variant="outline" onClick={() => handleSave("reviewed")} disabled={saving} className="rounded-lg text-sm">Mark as reviewed</Button>
            <Button variant="ghost" onClick={() => setShowNew(false)} className="text-sm">Cancel</Button>
          </div>
        </div>
      )}

      {/* Existing notes */}
      {(consultation_notes || []).length > 0 ? (
        <div className="space-y-2">
          {consultation_notes.map((n, i) => (
            <div key={i} className="border border-stone-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${statusColor[n.status] || ""}`}>{n.status}</Badge>
                  <span className="text-xs text-stone-400 capitalize">{n.consultation_type?.replace(/_/g, " ")}</span>
                </div>
                <span className="text-xs text-stone-400">{n.consultation_date?.split("T")[0] || n.created_at?.split("T")[0]}</span>
              </div>
              {n.nutrition_assessment && <p className="text-sm text-stone-600 line-clamp-2">{n.nutrition_assessment}</p>}
              {n.nutrition_intervention && <p className="text-sm text-stone-500 mt-1 line-clamp-2">{n.nutrition_intervention}</p>}
            </div>
          ))}
        </div>
      ) : !showNew && (
        <p className="text-sm text-stone-400 text-center py-4">No clinical notes yet. Create one above.</p>
      )}
    </div>
  );
};


const MealPlanGuides = ({ planData }) => {
  const [showGuides, setShowGuides] = React.useState(false);
  if (!planData) return null;
  const { core_goals, health_snapshot, plate_method, eat_more_of, eat_less_of, meal_routine, portion_option_guide } = planData;
  const hasGuides = core_goals?.length || health_snapshot?.length || plate_method || eat_more_of?.length || meal_routine?.length;
  if (!hasGuides) return null;

  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid="meal-plan-guides">
      <button onClick={() => setShowGuides(p => !p)} className="w-full flex items-center justify-between px-3 py-2.5 bg-[#FFF8F2] hover:bg-orange-50 transition-colors text-left">
        <span className="text-xs font-bold text-[#FF7A00]">Personalised Guides and Instructions</span>
        <span className="text-xs text-stone-400">{showGuides ? "Hide" : "Show"}</span>
      </button>
      {showGuides && (
        <div className="p-3 space-y-4 text-xs">
          {/* Core Goals */}
          {core_goals?.length > 0 && (
            <div className="bg-green-50 border border-green-100 rounded-lg p-3" data-testid="guide-core-goals">
              <p className="font-bold text-green-800 mb-1.5">Core Goals of This Meal Guide</p>
              <ul className="list-disc pl-4 space-y-0.5 text-green-700">
                {core_goals.map((g, i) => <li key={i}>{g}</li>)}
              </ul>
            </div>
          )}

          {/* Health Snapshot */}
          {health_snapshot?.length > 0 && (
            <div data-testid="guide-health-snapshot">
              <p className="font-bold text-stone-700 mb-1.5">What We Found</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {health_snapshot.map((m, i) => (
                  <div key={i} className="flex justify-between border-b border-stone-100 py-0.5">
                    <span className="text-stone-500">{m.label}</span>
                    <span className="font-medium text-stone-800">{m.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Plate Method */}
          {plate_method && (
            <div className="bg-orange-50 border border-orange-100 rounded-lg p-3" data-testid="guide-plate-method">
              <p className="font-bold text-[#FF7A00] mb-1">{plate_method.title || "The Plate Method"}</p>
              <p className="text-stone-500 mb-2">{plate_method.description}</p>
              <div className="space-y-1.5">
                {(plate_method.sections || []).map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="font-bold text-[#FF7A00] shrink-0 w-28">{s.portion}</span>
                    <span><strong>{s.food_group}</strong>: {s.examples}</span>
                  </div>
                ))}
              </div>
              {plate_method.hand_portions?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-orange-200">
                  <p className="font-semibold text-stone-600 mb-1">Hand-Sized Portions</p>
                  <div className="grid grid-cols-2 gap-1">
                    {plate_method.hand_portions.map((h, i) => (
                      <span key={i}><strong>{h.food_group}</strong>: {h.size}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Eat More / Less */}
          <div className="grid sm:grid-cols-2 gap-3">
            {eat_more_of?.length > 0 && (
              <div data-testid="guide-eat-more">
                <p className="font-bold text-green-700 mb-1.5">Eat More Of</p>
                <div className="space-y-1.5">
                  {eat_more_of.map((e, i) => (
                    <div key={i}>
                      <p className="font-semibold text-green-700">{e.category}</p>
                      <p className="text-stone-500">{e.items}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {eat_less_of?.length > 0 && (
              <div data-testid="guide-eat-less">
                <p className="font-bold text-red-700 mb-1.5">Eat Less Of</p>
                <div className="space-y-1.5">
                  {eat_less_of.map((e, i) => (
                    <div key={i}>
                      <p className="font-semibold text-red-700">{e.category}</p>
                      <p className="text-stone-500">{e.items}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Meal Routine */}
          {meal_routine?.length > 0 && (
            <div className="bg-stone-50 rounded-lg p-3" data-testid="guide-meal-routine">
              <p className="font-bold text-stone-700 mb-1.5">Meal Routine</p>
              <ul className="list-disc pl-4 space-y-0.5 text-stone-600">
                {meal_routine.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}

          {/* Portion Option Guide */}
          {portion_option_guide?.length > 0 && (
            <div data-testid="guide-portions">
              <p className="font-bold text-[#FF7A00] mb-1.5">Meal Portion Option Guide</p>
              <div className="space-y-2">
                {portion_option_guide.map((pg, i) => (
                  <div key={i}>
                    <p className="font-semibold text-stone-600">{pg.category}</p>
                    <ul className="list-disc pl-4 text-stone-500">
                      {(pg.options || []).map((o, j) => <li key={j}>{o}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


export const MealPlansSection = ({ meal_plans, patientId, token, onRefresh, s, standalone = false, initialPlanId = null, onClose }) => {
  const [generating, setGenerating] = useState(false);
  const [genForm, setGenForm] = useState({
    energy_target: 1800, allergies: "", medical_condition: "", dietitian_instructions: "",
  });
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [summaryKey, setSummaryKey] = useState(0);
  const [editingPlan, setEditingPlan] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const hdrs = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => { fetchPlans(); fetchTemplates(); if (initialPlanId) viewPlan(initialPlanId); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API}/staff/condition-templates`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setTemplates(d.templates || []); }
    } catch (e) { console.error(e); }
  };

  const applyTemplate = (tpl) => {
    setGenForm({
      energy_target: tpl.energy_target || 1800,
      allergies: (tpl.allergies || []).join(", "),
      medical_condition: tpl.condition || "",
      dietitian_instructions: tpl.dietitian_instructions || "",
    });
    setShowTemplates(false);
    toast.success(`Template "${tpl.name}" applied.`);
    // Record usage
    fetch(`${API}/staff/condition-templates/${tpl.template_id}/use`, { method: "POST", headers: hdrs }).catch(() => {});
  };

  const saveAsTemplate = async () => {
    if (!genForm.medical_condition) { toast.error("Enter a medical condition first."); return; }
    setSavingTemplate(true);
    try {
      const res = await fetch(`${API}/staff/condition-templates`, {
        method: "POST", headers: hdrs,
        body: JSON.stringify({
          name: `${genForm.medical_condition} - ${genForm.energy_target} kcal`,
          condition: genForm.medical_condition,
          energy_target: genForm.energy_target,
          allergies: genForm.allergies ? genForm.allergies.split(",").map(a => a.trim()) : [],
          dietitian_instructions: genForm.dietitian_instructions,
        }),
      });
      if (res.ok) { toast.success("Template saved."); fetchTemplates(); }
      else toast.error("Failed to save template.");
    } catch { toast.error("Connection error."); }
    finally { setSavingTemplate(false); }
  };

  const deleteTemplate = async (tplId) => {
    try {
      const res = await fetch(`${API}/staff/condition-templates/${tplId}`, { method: "DELETE", headers: hdrs });
      if (res.ok) { toast.success("Template deleted."); fetchTemplates(); }
    } catch { toast.error("Delete failed."); }
  };

  const [sigRequest, setSigRequest] = useState(null);
  const downloadPdf = (planId) => setSigRequest({ purpose: "meal_plan", docLabel: "meal plan PDF", actionLabel: "Download PDF", onConfirm: (sid) => doDownloadPdf(planId, sid) });
  const doDownloadPdf = async (planId, signatureId) => {
    try {
      const res = await fetch(`${API}/staff/meal-plans/${planId}/export-pdf?signature_id=${signatureId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const cd = res.headers.get("Content-Disposition") || "";
        const match = cd.match(/filename\*=UTF-8''([^;]+)/) || cd.match(/filename="([^"]+)"/);
        a.download = match ? decodeURIComponent(match[1]) : `${s?.name || "Patient"} - Personalised Meal Plan.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("PDF downloaded.");
      } else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "PDF export failed."); }
    } catch { toast.error("Connection error."); }
  };

  const fetchPlans = async () => {
    setLoadingPlans(true);
    try {
      const res = await fetch(`${API}/staff/patient/${patientId}/generated-plans`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPlans(d.plans || []); }
    } catch (e) { console.error(e); }
    finally { setLoadingPlans(false); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const prefs = s?.meal_preferences?.selected_foods || {};
      const prefStr = Object.entries(prefs).filter(([,v]) => v?.length).map(([cat, foods]) => `${cat}: ${foods.join(", ")}`).join("; ");

      const res = await fetch(`${API}/staff/meal-plans/generate`, {
        method: "POST", headers: hdrs,
        body: JSON.stringify({
          patient_id: patientId,
          energy_target: genForm.energy_target,
          allergies: genForm.allergies ? genForm.allergies.split(",").map(a => a.trim()) : [],
          food_preferences: prefStr,
          cultural_preferences: prefStr ? "Use the patient's selected food preferences above." : "",
          dietitian_instructions: genForm.dietitian_instructions,
          medical_condition: genForm.medical_condition,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${genForm.energy_target} kcal meal plan generated.`);
        fetchPlans();
      } else { const err = await res.json(); toast.error(err.detail || "Generation failed."); }
    } catch { toast.error("Connection error."); }
    finally { setGenerating(false); }
  };

  const viewPlan = async (planId) => {
    try {
      const res = await fetch(`${API}/staff/meal-plans/${planId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSelectedPlan(data);
        setEditingPlan(JSON.parse(JSON.stringify(data)));
      }
    } catch { toast.error("Could not load plan."); }
  };

  const updateMealField = (dayIdx, mealType, field, value) => {
    setEditingPlan(prev => {
      const updated = { ...prev };
      const days = [...(updated.plan_data?.days || [])];
      if (days[dayIdx]?.meals?.[mealType]) {
        days[dayIdx] = { ...days[dayIdx], meals: { ...days[dayIdx].meals, [mealType]: { ...days[dayIdx].meals[mealType], [field]: value } } };
      }
      updated.plan_data = { ...updated.plan_data, days };
      return updated;
    });
  };

  const updateMealItem = (dayIdx, mealType, itemIdx, field, value) => {
    setEditingPlan(prev => {
      const updated = { ...prev };
      const days = [...(updated.plan_data?.days || [])];
      const items = [...(days[dayIdx]?.meals?.[mealType]?.items || [])];
      items[itemIdx] = { ...items[itemIdx], [field]: value };
      days[dayIdx] = { ...days[dayIdx], meals: { ...days[dayIdx].meals, [mealType]: { ...days[dayIdx].meals[mealType], items } } };
      updated.plan_data = { ...updated.plan_data, days };
      return updated;
    });
  };

  const onMealSwapped = (dayIdx, mealType, meal, newPlanId) => {
    if (newPlanId && newPlanId !== editingPlan.plan_id) { viewPlan(newPlanId); fetchPlans(); return; }
    updateMealField(dayIdx, mealType, "name", meal.name);
    updateMealField(dayIdx, mealType, "items", meal.items);
    setSummaryKey(k => k + 1);
  };

  const savePlanEdits = async () => {
    if (!editingPlan?.plan_id) return;
    try {
      const res = await fetch(`${API}/staff/meal-plans/${editingPlan.plan_id}/edit`, {
        method: "PUT", headers: hdrs,
        body: JSON.stringify({ plan_data: editingPlan.plan_data }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.plan_id && d.plan_id !== editingPlan.plan_id) { toast.success(d.message); fetchPlans(); viewPlan(d.plan_id); }
      else if (res.ok) { toast.success("Meal plan saved."); setSelectedPlan(editingPlan); fetchPlans(); }
      else toast.error("Save failed.");
    } catch { toast.error("Connection error."); }
  };

  const sendPlanToPatient = (planId) => setSigRequest({ purpose: "meal_plan", docLabel: "meal plan and email", actionLabel: "Send to patient", onConfirm: (sid) => doSendPlan(planId, sid) });
  const doSendPlan = async (planId, signatureId) => {
    setSending(true);
    try {
      const res = await fetch(`${API}/staff/meal-plans/${planId}/send`, {
        method: "POST", headers: hdrs,
        body: JSON.stringify({ patient_id: patientId, signature_id: signatureId }),
      });
      if (res.ok) { const d = await res.json(); d.sent_via_email ? toast.success("Meal plan sent to patient.") : toast.error("Saved as sent, but the email could not be delivered. Check the patient's email address."); fetchPlans(); if (editingPlan?.plan_id === planId) viewPlan(planId); }
      else { const err = await res.json(); toast.error(err.detail || "Send failed."); }
    } catch { toast.error("Connection error."); }
    finally { setSending(false); }
  };

  const totalPrefs = Object.values(s?.meal_preferences?.selected_foods || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  return (
    <div className="space-y-4">
      <SignaturePicker request={sigRequest} token={token} onClose={() => setSigRequest(null)} />
      <PatientSwapRequests patientId={patientId} token={token} onApplied={(pid) => { fetchPlans(); setSummaryKey(k => k + 1); if (editingPlan) viewPlan(pid || editingPlan.plan_id); }} />
      <AvoidedMeals patientId={patientId} token={token} refreshKey={summaryKey} />
      {!standalone && <>
      {/* Condition Templates */}
      {templates.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="condition-templates">
          <span className="text-xs font-medium text-stone-500">Templates:</span>
          {templates.map(tpl => (
            <div key={tpl.template_id} className="flex items-center gap-0.5">
              <button
                onClick={() => applyTemplate(tpl)}
                className="text-xs px-2.5 py-1 rounded-full bg-stone-100 hover:bg-[#FF7A00]/10 hover:text-[#FF7A00] text-stone-600 transition-colors"
                data-testid={`template-${tpl.template_id}`}
              >
                {tpl.name}
              </button>
              <button onClick={() => deleteTemplate(tpl.template_id)} className="text-stone-300 hover:text-red-500 text-xs px-0.5" title="Delete template">&times;</button>
            </div>
          ))}
        </div>
      )}

      {/* Calorie selector + generate - always visible */}
      <div className="bg-stone-50 rounded-lg p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <Field label="Calorie target" className="flex-1">
            <Select value={String(genForm.energy_target)} onValueChange={v => setGenForm(p => ({ ...p, energy_target: parseInt(v) }))}>
              <SelectTrigger className="rounded-lg bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1200, 1500, 1800, 2000, 2200, 2500].map(c => (
                  <SelectItem key={c} value={String(c)}>{c} kcal</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Medical condition" className="flex-1">
            <Input placeholder="e.g. Type 2 diabetes, hypertension" value={genForm.medical_condition} onChange={e => setGenForm(p => ({ ...p, medical_condition: e.target.value }))} className="rounded-lg bg-white" />
          </Field>
          <Button onClick={handleGenerate} disabled={generating} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg text-sm h-10 shrink-0" data-testid="generate-plan-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />} Generate
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input placeholder="Allergies (comma-separated)" value={genForm.allergies} onChange={e => setGenForm(p => ({ ...p, allergies: e.target.value }))} className="rounded-lg bg-white text-sm" />
          <Input placeholder="Dietitian instructions (optional)" value={genForm.dietitian_instructions} onChange={e => setGenForm(p => ({ ...p, dietitian_instructions: e.target.value }))} className="rounded-lg bg-white text-sm" />
        </div>
        <div className="flex items-center gap-2">
          {totalPrefs > 0 && <p className="text-xs text-green-600 flex-1">{totalPrefs} food preferences will be included in plan generation.</p>}
          <button onClick={saveAsTemplate} disabled={savingTemplate} className="text-xs text-stone-400 hover:text-[#FF7A00] transition-colors" data-testid="save-template-btn">
            {savingTemplate ? "Saving..." : "Save as template"}
          </button>
        </div>
      </div>
      </>}

      {/* Plan viewer - EDITABLE with personalised guides */}
      {editingPlan && (
        <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-stone-800">{editingPlan.energy_target} kcal - {editingPlan.plan_data?.days?.length || 0} days</h4>
            <div className="flex gap-2">
              <Badge className={`text-xs ${editingPlan.status === "approved" ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}`}>{editingPlan.status}</Badge>
              <Button variant="ghost" size="sm" onClick={() => { if (onClose) { onClose(); return; } setSelectedPlan(null); setEditingPlan(null); }} className="text-xs" data-testid="close-plan-btn">Close</Button>
            </div>
          </div>
          <div className="max-h-[600px] overflow-y-auto space-y-3 pr-1">
            <PreferenceSummary planId={editingPlan.plan_id} token={token} refreshKey={summaryKey} />
            {/* Personalised Guides Section */}
            <MealPlanGuides planData={editingPlan.plan_data} />

            {/* Daily Meals */}
            {(editingPlan.plan_data?.days || []).map((day, di) => (
              <div key={di} className="border border-stone-100 rounded-lg p-3">
                <p className="text-xs font-bold text-stone-700 mb-2">Day {day.day || di + 1} - {day.day_name || ""}</p>
                {Object.entries(day.meals || {}).map(([mealType, meal]) => (
                  <div key={mealType} className="mb-3 last:mb-0 pl-2 border-l-2 border-[#FF7A00]/20">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-[#FF7A00] capitalize">{mealType} {meal.time ? `(${meal.time})` : ""}</p>
                      <MealSwapPicker planId={editingPlan.plan_id} token={token} day={day.day || di + 1} mealType={mealType} onSwapped={(meal, pid) => onMealSwapped(di, mealType, meal, pid)} />
                    </div>
                    <Input value={meal.name || ""} onChange={e => updateMealField(di, mealType, "name", e.target.value)} className="rounded-lg text-sm h-8 mb-1 font-medium" placeholder="Meal name" />
                    {(meal.items || []).map((item, ii) => (
                      <div key={ii} className="flex gap-1.5 items-center ml-2 mb-0.5">
                        <Input value={item.qty || ""} onChange={e => updateMealItem(di, mealType, ii, "qty", e.target.value)} className="rounded text-xs h-7 w-32" placeholder="Qty" />
                        <Input value={item.food_name ?? item.food_id?.replace(/^f_/, "").replace(/_/g, " ") ?? ""} onChange={e => updateMealItem(di, mealType, ii, "food_name", e.target.value)} className="rounded text-xs h-7 flex-1" placeholder="Food" />
                        <Input value={item.note || ""} onChange={e => updateMealItem(di, mealType, ii, "note", e.target.value)} className="rounded text-xs h-7 w-32" placeholder="Note" />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-100">
            <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={savePlanEdits}>
              <Save className="w-3 h-3 mr-1" /> Save edits
            </Button>
            {editingPlan.status === "draft" && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs" onClick={async () => {
                try {
                  const res = await fetch(`${API}/staff/meal-plans/${editingPlan.plan_id}/status`, { method: "PUT", headers: hdrs, body: JSON.stringify({ status: "approved" }) });
                  if (res.ok) { toast.success("Meal plan approved."); fetchPlans(); setEditingPlan(p => ({...p, status: "approved"})); }
                } catch { toast.error("Approval failed."); }
              }}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
              </Button>
            )}
            <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => sendPlanToPatient(editingPlan.plan_id)} disabled={sending} data-testid="send-plan-to-patient">
              <MessageSquare className="w-3 h-3 mr-1" /> {sending ? "Sending..." : "Send via Email"}
            </Button>
            <Button variant="outline" size="sm" className="text-xs rounded-lg text-green-700 border-green-200 hover:bg-green-50" onClick={async () => {
              const name = (s?.name || "there").split(" ")[0];
              const phone = (s?.phone || "").replace(/[^0-9]/g, "");
              if (!phone) { toast.error("No phone number for this patient."); return; }
              const win = window.open("", "_blank");
              let link = "";
              try { const r = await fetch(`${API}/staff/meal-plans/${editingPlan.plan_id}/patient-link`, { headers: hdrs }); if (r.ok) link = (await r.json()).url; } catch { /* send without link */ }
              const msg = `Hello ${name},\n\nYour personalised meal plan is ready.\n\nInside, you'll find your recommended meals, portion guidance, meal schedule and practical food suggestions tailored to your nutrition goals and preferences.\n\nPlease check your email for the PDF attachment. If you haven't received it, let us know and we'll resend it.${link ? `\n\nView your plan online, and ask us to swap any meal you don't like or can't easily get:\n${link}` : ""}\n\nPlease contact your dietitian if you have questions.\n\nPlateMind Nutrition Team`;
              const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
              if (win) win.location.href = url; else window.open(url, "_blank");
            }} data-testid="send-plan-whatsapp">
              <Phone className="w-3 h-3 mr-1" /> Send via WhatsApp
            </Button>
            <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => downloadPdf(editingPlan.plan_id)} data-testid="download-pdf-btn">
              <FileText className="w-3 h-3 mr-1" /> Download PDF
            </Button>
          </div>
        </div>
      )}

      {/* Existing plans */}
      {standalone ? null : loadingPlans ? (
        <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto" /></div>
      ) : plans.length > 0 ? (
        <div className="space-y-2">
          {plans.map((p, i) => (
            <div key={i} className="border border-stone-200 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-stone-50" onClick={() => viewPlan(p.plan_id)}>
              <div>
                <p className="text-sm font-medium text-stone-900">{p.energy_target || 1800} kcal - {p.plan_data?.days?.length || 14} day plan</p>
                <p className="text-xs text-stone-400">{p.status} - {p.created_at?.split("T")[0]}</p>
              </div>
              <Badge className={`text-xs ${p.status === "approved" ? "bg-green-50 text-green-700" : p.status === "sent" ? "bg-blue-50 text-blue-700" : "bg-yellow-50 text-yellow-700"}`}>{p.status || "draft"}</Badge>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-stone-400 text-center py-4">No meal plans generated yet.</p>
      )}
    </div>
  );
};

const ReportsSection = ({ patientId, token, onRefresh, patient, s }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGen, setShowGen] = useState(false);
  const [genType, setGenType] = useState("participant");
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [sending, setSending] = useState(false);

  const hdrs = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => { fetchReports(); }, [patientId]);

  const [linkParams] = useSearchParams();
  const linkedReport = linkParams.get("report");
  useEffect(() => {
    if (!linkedReport) return;
    viewReport(linkedReport).then(() => setTimeout(() => document.getElementById("reports")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300));
  }, [linkedReport]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/staff/patient/${patientId}/reports`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setReports(d.reports || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleGenerate = async (type) => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/staff/reports/generate`, {
        method: "POST", headers: hdrs,
        body: JSON.stringify({ patient_id: patientId, report_type: type }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${type === "participant" ? "Patient" : "Doctor"} report generated.`);
        setShowGen(false);
        fetchReports();
      } else { const err = await res.json(); toast.error(err.detail || "Generation failed."); }
    } catch { toast.error("Connection error."); }
    finally { setGenerating(false); }
  };

  const viewReport = async (reportId) => {
    try {
      const res = await fetch(`${API}/staff/reports/${reportId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setSelectedReport(await res.json());
    } catch { toast.error("Could not load report."); }
  };

  const approveReport = async (reportId) => {
    try {
      const res = await fetch(`${API}/staff/reports/${reportId}/approve`, { method: "POST", headers: hdrs });
      if (res.ok) { toast.success("Report approved."); fetchReports(); if (selectedReport?.report_id === reportId) viewReport(reportId); }
    } catch { toast.error("Approval failed."); }
  };

  const [sigRequest, setSigRequest] = useState(null);
  const sendReport = (reportId, channel, recipientType) => setSigRequest({
    purpose: recipientType === "doctor" ? "doctor_report" : "patient_report", docLabel: "report and email",
    actionLabel: recipientType === "doctor" ? "Send to doctor" : "Send to patient", onConfirm: (sid) => doSendReport(reportId, channel, recipientType, sid) });
  const doSendReport = async (reportId, channel, recipientType, signatureId) => {
    setSending(true);
    try {
      const res = await fetch(`${API}/staff/reports/${reportId}/send`, {
        method: "POST", headers: hdrs,
        body: JSON.stringify({ channel, recipient_type: recipientType, signature_id: signatureId }),
      });
      if (res.ok) { toast.success(`Report sent via ${channel}.`); fetchReports(); }
      else {
        const err = await res.json();
        if (res.status === 403 && recipientType === "doctor") {
          toast.error("Cannot send to doctor: patient has not given consent to share reports with their doctor. Please obtain consent in the pre-consultation form first.", { duration: 8000 });
        } else {
          toast.error(err.detail || "Send failed.");
        }
      }
    } catch { toast.error("Connection error."); }
    finally { setSending(false); }
  };

  if (loading) return <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto" /></div>;

  return (
    <div className="space-y-4">
      <SignaturePicker request={sigRequest} token={token} onClose={() => setSigRequest(null)} />
      {/* Generate report - single button with type selector */}
      <div className="flex items-center gap-3 bg-stone-50 rounded-lg p-3">
        <Select value={genType} onValueChange={setGenType}>
          <SelectTrigger className="rounded-lg bg-white w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="participant">Patient report</SelectItem>
            <SelectItem value="doctor">Doctor report</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => handleGenerate(genType)} disabled={generating} data-testid="gen-report">
          {generating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileText className="w-3 h-3 mr-1" />} Generate report
        </Button>
      </div>

      {/* Report viewer - editable */}
      {selectedReport && (
        <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-bold text-stone-800">{selectedReport.title}</h4>
              <p className="text-xs text-stone-400">{selectedReport.report_type} - {selectedReport.status} - {selectedReport.created_at?.split("T")[0]}</p>
            </div>
            <div className="flex gap-2">
              <Badge className={`text-xs ${selectedReport.status === "approved" ? "bg-green-50 text-green-700" : selectedReport.status === "sent" ? "bg-blue-50 text-blue-700" : "bg-yellow-50 text-yellow-700"}`}>{selectedReport.status}</Badge>
              {canDeleteReport(selectedReport) && <Button variant="ghost" size="sm" className="text-xs text-red-600 hover:bg-red-50" onClick={async () => { if (await deleteReportWithUndo(token, selectedReport, fetchReports)) setSelectedReport(null); }} data-testid="profile-delete-report"><Trash2 className="w-3 h-3 mr-1" />Delete</Button>}
              <Button variant="ghost" size="sm" onClick={() => setSelectedReport(null)} className="text-xs">Close</Button>
            </div>
          </div>
          <Textarea
            value={selectedReport.content || ""}
            onChange={e => setSelectedReport(prev => ({ ...prev, content: e.target.value }))}
            className="rounded-lg min-h-[300px] text-sm leading-relaxed font-mono"
          />
          <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-100">
            <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={async () => {
              try {
                const res = await fetch(`${API}/staff/reports/${selectedReport.report_id}/edit`, {
                  method: "PUT", headers: hdrs,
                  body: JSON.stringify({ content: selectedReport.content }),
                });
                if (res.ok) { toast.success("Report saved."); fetchReports(); }
              } catch { toast.error("Save failed."); }
            }}>
              <Save className="w-3 h-3 mr-1" /> Save edits
            </Button>
            {selectedReport.status !== "approved" && selectedReport.status !== "sent" && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs" onClick={() => approveReport(selectedReport.report_id)}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
              </Button>
            )}
            <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => sendReport(selectedReport.report_id, "email", "patient")} disabled={sending} data-testid="send-to-patient">
              <MessageSquare className="w-3 h-3 mr-1" /> Email to patient
            </Button>
            <Button variant="outline" size="sm" className="text-xs rounded-lg text-green-700 border-green-200 hover:bg-green-50" onClick={() => {
              const name = s?.name || patient?.name || "there";
              const type = selectedReport.report_type === "doctor" ? "clinical" : "consultation";
              openWhatsApp(s?.phone || patient?.phone, `Hello ${name},\n\nYour clinical nutrition report is ready.\n\nThe report summarises your nutrition assessment, relevant health measurements, clinical nutrition priorities and recommended next steps. Where applicable, it may also include recommendations intended to support coordinated care with your primary physician.\n\nPlease check your email for the PDF attachment. If you haven't received it, let us know and we'll resend it.\n\nPlease review the document and contact your dietitian if you need clarification.\n\nPlateMind Nutrition Team`);
            }} data-testid="send-report-whatsapp">
              <Phone className="w-3 h-3 mr-1" /> WhatsApp to patient
            </Button>
            <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => sendReport(selectedReport.report_id, "email", "doctor")} disabled={sending} data-testid="send-to-doctor">
              <Stethoscope className="w-3 h-3 mr-1" /> Email to doctor
            </Button>
            <Button variant="outline" size="sm" className="text-xs rounded-lg text-green-700 border-green-200 hover:bg-green-50" onClick={() => {
              const docPhone = s?.preconsult?.doctor_phone || s?.preconsult?.doctor_contact || "";
              const docName = s?.preconsult?.doctor_name || "Doctor";
              const patientName = s?.name || patient?.name || "the patient";
              openWhatsApp(docPhone, `Dear ${docName},\n\nPlease find attached the clinical nutrition report for your patient, ${patientName}.\n\nKindly review at your earliest convenience. If you have any questions or require further information, please feel free to reply here.\n\nKind regards,\nPlateMind Nutrition`);
            }} data-testid="send-doctor-whatsapp">
              <Phone className="w-3 h-3 mr-1" /> WhatsApp to doctor
            </Button>
            <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => setSigRequest({ purpose: selectedReport.report_type === "doctor" ? "doctor_report" : "patient_report", docLabel: "report PDF", actionLabel: "Download PDF", onConfirm: async (signatureId) => {
              try {
                const res = await fetch(`${API}/staff/reports/${selectedReport.report_id}/export-pdf?signature_id=${signatureId}`, { headers: { Authorization: `Bearer ${token}` } });
                if (res.ok) {
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  const cd = res.headers.get("Content-Disposition") || "";
                  const m = cd.match(/filename\*=UTF-8''([^;]+)/) || cd.match(/filename="([^"]+)"/);
                  a.download = m ? decodeURIComponent(m[1]) : `${selectedReport.patient_name || "Patient"} - Clinical Report.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("PDF downloaded.");
                } else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "PDF export failed."); }
              } catch { toast.error("Connection error."); }
            }})} data-testid="download-report-pdf">
              <Download className="w-3 h-3 mr-1" /> Download PDF
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      {reports.length > 0 ? (
        <div className="space-y-2">
          {reports.map((r, i) => (
            <div key={i} className="border border-stone-200 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-stone-50" onClick={() => viewReport(r.report_id)}>
              <div>
                <p className="text-sm font-medium text-stone-900">{r.title || r.report_type?.replace(/_/g, " ")}</p>
                <p className="text-xs text-stone-400">{r.report_type} - {r.created_at?.split("T")[0]} - by {r.created_by_name || "Staff"}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`text-xs ${r.status === "approved" ? "bg-green-50 text-green-700" : r.status === "sent" ? "bg-blue-50 text-blue-700" : "bg-yellow-50 text-yellow-700"}`}>{r.status || "draft"}</Badge>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-stone-400 text-center py-4">No reports generated yet. Generate a patient or doctor report above.</p>
      )}
    </div>
  );
};

const CommunicationsSection = ({ communications, intake_schedules }) => {
  const allEvents = [
    ...(communications || []).map(c => ({
      type: c.event_type || "message", channel: c.channel || "system", status: c.status,
      detail: c.message_template || c.event_type || "", date: c.created_at,
    })),
    ...(intake_schedules || []).map(s => ({
      type: "intake_form", channel: "link", status: s.send_status,
      detail: s.form_completed ? "Intake form completed" : `Intake form ${s.send_status}`, date: s.actually_sent_at || s.created_at,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  const statusColor = { sent: "bg-green-50 text-green-700", delivered: "bg-green-50 text-green-700", failed: "bg-red-50 text-red-700", pending: "bg-yellow-50 text-yellow-700" };

  return allEvents.length > 0 ? (
    <div className="space-y-1">
      {allEvents.slice(0, 15).map((ev, i) => (
        <div key={i} className="flex items-center justify-between py-2 border-b border-stone-50 last:border-0">
          <div className="flex items-center gap-2 min-w-0">
            <Badge variant="outline" className={`text-[10px] ${statusColor[ev.status] || ""}`}>{ev.status}</Badge>
            <span className="text-sm text-stone-600 truncate capitalize">{ev.type.replace(/_/g, " ")}</span>
            <span className="text-xs text-stone-400 capitalize">{ev.channel}</span>
          </div>
          <span className="text-xs text-stone-400 shrink-0">{ev.date?.split("T")[0]}</span>
        </div>
      ))}
    </div>
  ) : (
    <p className="text-sm text-stone-400 text-center py-4">No communication events yet.</p>
  );
};

// ===== PROGRAMME TIMELINE (toggle) =====
const ProgrammeTimeline = ({ patient, consultation_notes, observations, bookings, assessments, intake_schedules, enrolments }) => {
  const enrolmentDate = patient.created_at ? new Date(patient.created_at) : null;
  const getWeekNumber = () => {
    if (!enrolmentDate) return null;
    const diffWeeks = Math.floor((Date.now() - enrolmentDate.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
    return Math.min(diffWeeks, 12);
  };
  const currentWeek = getWeekNumber();

  return (
    <Card className="rounded-xl border-stone-200/60 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold text-stone-900">12-Week Programme Timeline</h3>
        {currentWeek && <Badge className="bg-[#FF7A00]/10 text-[#FF7A00] border-[#FF7A00]/20 text-xs">Week {currentWeek} of 12</Badge>}
      </div>
      {currentWeek && (
        <div className="w-full bg-stone-100 rounded-full h-2">
          <div className="bg-[#FF7A00] h-2 rounded-full transition-all" style={{ width: `${(currentWeek / 12) * 100}%` }} />
        </div>
      )}
      <p className="text-xs text-stone-500 mt-2">Enrolled: {enrolmentDate?.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) || "Unknown"}</p>
    </Card>
  );
};

export default PatientProfile;
